/**
 * Seed Data Utilities
 *
 * Shared utilities for generating deterministic seed data.
 * Used across all domain seed files.
 */

/**
 * Generate a deterministic valid UUID for seed data.
 *
 * Uses a hash-based approach to create consistent UUIDs from a prefix and index.
 * This ensures the same seed data generates the same UUIDs across runs,
 * enabling idempotent seeding.
 *
 * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx (UUID v4 format)
 *
 * @param prefix - A string prefix to identify the entity type (e.g., 'user', 'consumer')
 * @param index - A unique index within the entity type
 * @returns A valid UUID v4 format string
 *
 * @example
 * ```ts
 * seedId('user', 1)  // → '04b5e034-0001-4009-8001-000004b5d00d'
 * seedId('user', 2)  // → '04b5e034-0002-4009-8002-000004b5d00e'
 * ```
 */
export function seedId(prefix: string, index: number): string {
  // Create a deterministic seed string
  const seedString = `${prefix}-${index}`;

  // Simple hash function to generate hex digits
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    const char = seedString.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to positive hex string and pad
  const hex = Math.abs(hash).toString(16).padStart(8, '0');

  // Create deterministic UUID parts based on prefix and index
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  const part1 = hex.slice(0, 8);
  const part2 = index.toString(16).padStart(4, '0');
  const part3 = `4${prefix.length.toString(16).padStart(3, '0')}`; // Version 4
  const part4 = `8${(index % 4096).toString(16).padStart(3, '0')}`; // Variant
  const part5 = (Math.abs(hash) ^ (index * 12345)).toString(16).padStart(12, '0').slice(0, 12);

  return `${part1}-${part2}-${part3}-${part4}-${part5}`;
}

/**
 * Default password for all seed users.
 * Used across admin, business, and consumer portals for demo/testing.
 */
export const DEFAULT_PASSWORD = 'Demo123!';
